/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, onConnect, withAssign } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { AsyncStateN, pipe, Result } from '@blade/std-lib'
import { CurrencyModel } from '@root/entities/currency'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { nativePriceByNetwork } from '@root/features/realtime/atoms'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { reatomRequest } from '@root/shared/lib'

const NULL = 'null'
type Price = {
  priceUsd: BigNumber
}

export const fetchCurrencyPriceRequestAction = action(
  async (
    ctx,
    { address, network }: { address: string; network: Network.Network },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const priceResult = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.services.servicesPriceList, [
        {
          network: Network.toDto(network),
          token: address,
        },
      ]),
    )

    return pipe(
      priceResult,
      Result.map((it): Price => ({ priceUsd: BigNumber(it.price) })),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'fetchCurrencyPriceRequestAction',
)

const reatomCurrencyPriceInternal = ({
  address,
  initial,
}: {
  address: string
  initial?: Price
}) => {
  const loadAction = action(async (ctx) => {
    // Workaround bug with disconnection of dynamic atoms
    if (address === NULL) {
      const nullPrice: Price = { priceUsd: BigNumber(0) }
      return Result.succeed(nullPrice)
    }
    const network = ctx.get(selectedNetworkAtom)
    return fetchCurrencyPriceRequestAction(ctx, { address, network })
  }, `currencyPrice_${address}_loadInternalAction`)

  const {
    requestStateAtom: priceStateAtom,
    executeAction: loadPriceAction,
    resetStateAction,
  } = reatomRequest({
    execAction: loadAction,
    name: `currencyPrice_${address}`,
    initial: initial ? AsyncStateN.success(initial) : undefined,
  })

  onConnect(priceStateAtom, (ctx) => {
    if (address === NULL || initial) {
      return
    }
    loadPriceAction(ctx)
  })

  return priceStateAtom.pipe(
    withAssign(() => ({ load: loadPriceAction, resetState: resetStateAction })),
  )
}

type CurrencyPriceInternalReatom = ReturnType<
  typeof reatomCurrencyPriceInternal
>

const currentPriceRegistry = new Map<string, CurrencyPriceInternalReatom>([
  [NULL, reatomCurrencyPriceInternal({ address: NULL })],
])

export const reatomCurrencyPrice = ({
  currency,
}: {
  currency: CurrencyModel | undefined
}) => {
  const nativePriceAtom = atom((ctx) => {
    if (!currency) {
      return AsyncStateN.success({
        priceUsd: BigNumber(0),
      })
    }

    const priceUsd = ctx.spy(nativePriceByNetwork[currency.network])
    return AsyncStateN.success({
      priceUsd,
    })
  })
  if (currency && CurrencyModel.isNativeOrWrapped(currency.network)(currency)) {
    return nativePriceAtom
  }

  const resolvedAddress = currency?.address ?? NULL
  const existingAtom = currentPriceRegistry.get(resolvedAddress)
  if (existingAtom) {
    return existingAtom
  }
  const theAtom = reatomCurrencyPriceInternal({ address: resolvedAddress })
  currentPriceRegistry.set(resolvedAddress, theAtom)
  return theAtom
}
